import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Eye, ChevronUp, ChevronDown, Hand, X } from 'lucide-react';
import { useTenantAIConfig } from '@/hooks/useTenantAIConfig';
import { saveProspectListContext } from '@/hooks/useReturnNavigation';

const COLLAPSED_KEY = 'valnexo_uatu_greeting_collapsed_v2';
const CRITICAL_SIGNATURE_KEY = 'valnexo_uatu_last_critical_signature';

/**
 * 🧿 UatuGreetingBubble
 * ---------------------
 * Bulle conversationnelle Uatu (le Watcher Valnexo) qui s'adresse directement
 * à l'utilisateur en tête du Dashboard. Remplace visuellement l'ancien tableau
 * d'alertes figé — les DONNÉES sont identiques, mais présentées comme une
 * conversation avec Uatu.
 *
 * Comportement :
 *  - Affiche un message perso ("Salut Pierre 👋 Je vois X priorités...")
 *  - Les alertes deviennent des PILLS cliquables sous le message
 *  - Bouton "Passe-moi la main" → ouvre le chat Uatu avec le sujet pré-chargé
 *  - Réductible → mini bandeau (rebasculée si nouvelle alerte critical apparaît)
 *  - Nudges bienveillants tout au long de la session via UatuNudgeManager
 */
export default function UatuGreetingBubble() {
    const [briefing, setBriefing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
    });
    // Sollicitations Uatu — nb d'alertes non-vues (critical + important) exposé
    // en rappel subtil dans la bulle Uatu, sans widget dédié pour éviter le "trop".
    const [alertsSummary, setAlertsSummary] = useState({ unseen: 0, critical: 0 });
    const navigate = useNavigate();
    const tenantAI = useTenantAIConfig();

    // Charge le briefing et re-rafraîchit toutes les 5 min
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await api.get('/dashboard/ai-briefing');
                if (!mounted) return;
                setBriefing(res.data);
                // Si NOUVELLE alerte critical détectée → force la ré-ouverture de la bulle
                try {
                    const sig = JSON.stringify(
                        (res.data.priorities || [])
                            .filter((p) => p.level === 'critical')
                            .map((p) => p.id + ':' + p.count)
                    );
                    const lastSig = localStorage.getItem(CRITICAL_SIGNATURE_KEY) || '';
                    if (sig && sig !== '[]' && sig !== lastSig) {
                        // Nouvelle alerte critique → on ré-ouvre
                        localStorage.removeItem(COLLAPSED_KEY);
                        setCollapsed(false);
                        localStorage.setItem(CRITICAL_SIGNATURE_KEY, sig);
                    }
                } catch { /* noop */ }
            } catch (e) {
                console.error('uatu-greeting error', e);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        const loadAlerts = async () => {
            try {
                const res = await api.get('/uatu/observations');
                if (!mounted) return;
                const obs = res.data?.observations || [];
                const unseen = obs.filter((o) => !o.seen_at);
                setAlertsSummary({
                    unseen: unseen.length,
                    critical: unseen.filter((o) => o.severity === 'critical').length,
                });
            } catch { /* silencieux */ }
        };
        load();
        loadAlerts();
        const t = setInterval(load, 5 * 60 * 1000);
        const ta = setInterval(loadAlerts, 90 * 1000); // même cadence que la bulle chat
        return () => { mounted = false; clearInterval(t); clearInterval(ta); };
    }, []);

    // Ouvre le chat Uatu directement sur l'onglet Alertes
    const openAlerts = () => {
        try {
            window.dispatchEvent(new CustomEvent('uatu:open-alerts'));
        } catch { /* noop */ }
    };

    // Persiste le pli/dépli
    useEffect(() => {
        try {
            if (collapsed) localStorage.setItem(COLLAPSED_KEY, '1');
            else localStorage.removeItem(COLLAPSED_KEY);
        } catch { /* noop */ }
    }, [collapsed]);

    const openChatWithSeed = (seedMessage) => {
        // Réutilise le mécanisme existant (AIChatBubble écoute cet event)
        try {
            window.dispatchEvent(
                new CustomEvent('uatu:open-with-message', {
                    detail: { message: seedMessage || '' },
                })
            );
        } catch { /* noop */ }
    };

    if (loading) {
        return (
            <div className="rounded-3xl border border-violet-200/70 bg-gradient-to-br from-white to-violet-50/30 p-5 animate-pulse">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-violet-200/70" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3 bg-violet-100 rounded w-1/4" />
                        <div className="h-4 bg-slate-100 rounded w-4/5" />
                        <div className="h-3 bg-slate-100 rounded w-3/5" />
                    </div>
                </div>
            </div>
        );
    }
    if (!briefing) return null;

    const priorities = briefing.priorities || [];
    const criticalCount = briefing.critical_count || 0;
    const total = briefing.total_priorities || 0;

    // ====== VUE RÉDUITE — mini bandeau discret cliquable ======
    if (collapsed) {
        return (
            <button
                type="button"
                onClick={() => setCollapsed(false)}
                data-testid="uatu-greeting-collapsed"
                className="w-full rounded-2xl border border-violet-200 bg-white/80 backdrop-blur-sm hover:bg-violet-50 transition-all px-4 py-2.5 flex items-center gap-3 shadow-sm text-left group"
            >
                <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 relative shadow-inner"
                    style={{ background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #A855F7 100%)' }}
                >
                    <Eye className="w-4 h-4 text-white" />
                    {criticalCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white">
                            {criticalCount}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">
                        {tenantAI.assistant_name || 'Uatu'} · le Watcher
                    </p>
                    <p className="text-sm font-medium text-slate-700 truncate">
                        {total > 0
                            ? `${total} priorité${total > 1 ? 's' : ''} à regarder — clique pour voir`
                            : 'Tout est nickel — je veille pour toi'}
                    </p>
                </div>
                <ChevronDown className="w-5 h-5 text-violet-400 flex-shrink-0 group-hover:text-violet-700 transition-colors" />
            </button>
        );
    }

    // ====== VUE COMPLÈTE — bulle conversationnelle style chat ======
    return (
        <div
            data-testid="uatu-greeting-bubble"
            className="relative rounded-3xl overflow-hidden border border-violet-200/60 shadow-lg"
            style={{
                background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.02) 0%, rgba(168, 85, 247, 0.06) 100%)',
                boxShadow: '0 8px 32px rgba(124, 58, 237, 0.10), inset 0 1px 0 rgba(255,255,255,0.6)',
            }}
        >
            {/* Bouton réduire — discret en haut-droite */}
            <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/70 hover:bg-white text-slate-400 hover:text-violet-700 backdrop-blur-sm transition-all flex items-center justify-center shadow-sm"
                aria-label="Réduire la bulle Uatu"
                title="Réduire — les alertes reviendront si nouveau critique"
                data-testid="uatu-greeting-collapse"
            >
                <ChevronUp className="w-4 h-4" />
            </button>

            <div className="relative p-5 sm:p-6">
                <div className="flex items-start gap-4">
                    {/* Avatar Uatu — le Watcher */}
                    <div className="relative flex-shrink-0">
                        <div
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shadow-lg relative overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 40%, #7C3AED 100%)',
                                boxShadow: '0 8px 24px rgba(124, 58, 237, 0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
                            }}
                        >
                            {/* Effet œil/watcher */}
                            <Eye className="w-7 h-7 sm:w-8 sm:h-8 text-white relative z-10" strokeWidth={2.2} />
                            {/* Halo lumineux subtil */}
                            <div className="absolute inset-0 opacity-30" style={{
                                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6), transparent 60%)',
                            }} />
                        </div>
                        {/* Point actif pulsant */}
                        <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
                    </div>

                    {/* Bulle de message Uatu */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                            <p className="text-[10px] sm:text-xs font-bold text-violet-800 uppercase tracking-widest">
                                {tenantAI.assistant_name || 'Uatu'}
                            </p>
                            <p className="text-[10px] sm:text-xs text-violet-500 italic">
                                · le Watcher Valnexo · veille en direct
                            </p>
                        </div>

                        {/* Message conversationnel style chat */}
                        <div className="bg-white/90 backdrop-blur-sm rounded-2xl rounded-tl-md px-4 py-3 border border-violet-100 shadow-sm">
                            <p
                                className="text-sm sm:text-[15px] text-slate-800 leading-relaxed"
                                data-testid="uatu-message"
                                dangerouslySetInnerHTML={{
                                    __html: (briefing.uatu_message || '').replace(
                                        /\*\*(.*?)\*\*/g,
                                        '<strong class="text-violet-900 font-semibold">$1</strong>'
                                    ),
                                }}
                            />
                        </div>

                        {/* Priorités = pills cliquables sous le message */}
                        {priorities.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="uatu-priorities">
                                {priorities.map((p) => {
                                    const levelStyles = {
                                        critical: 'bg-red-50 border-red-200 text-red-800 hover:bg-red-100 hover:border-red-300',
                                        warning: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100 hover:border-orange-300',
                                        info: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100 hover:border-blue-300',
                                    };
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => {
                                                if (p.cta_url) {
                                                    // Si le CTA pointe vers une liste de prospects (avec ids=...),
                                                    // on sauve le contexte de liste pour que la barre "Précédent / Suivant"
                                                    // apparaisse en haut de chaque fiche prospect.
                                                    try {
                                                        const url = p.cta_url;
                                                        if (url.includes('/prospects') && url.includes('ids=')) {
                                                            const idsMatch = url.match(/[?&]ids=([^&]+)/);
                                                            if (idsMatch) {
                                                                const ids = decodeURIComponent(idsMatch[1])
                                                                    .split(',')
                                                                    .filter(Boolean);
                                                                if (ids.length >= 2) {
                                                                    saveProspectListContext(ids, null, {
                                                                        path: url,
                                                                        label: p.label || 'Retour à la liste Uatu',
                                                                    });
                                                                }
                                                            }
                                                        }
                                                    } catch { /* noop */ }
                                                    navigate(p.cta_url);
                                                }
                                            }}
                                            data-testid={`uatu-priority-${p.id}`}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${levelStyles[p.level] || levelStyles.info} shadow-sm hover:shadow`}
                                            title={p.label}
                                        >
                                            <span>{p.icon}</span>
                                            <span className="truncate max-w-[220px] sm:max-w-none">{p.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Rappel subtil — sollicitations Uatu (alertes non-vues).
                            S'affiche uniquement s'il y a des alertes non-vues, en petit
                            format sous les priorités. Design discret : point rouge + texte
                            + lien "Voir" qui ouvre l'onglet Alertes du chat. */}
                        {alertsSummary.unseen > 0 && (
                            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="uatu-alerts-nudge">
                                <span
                                    className={`inline-block w-2 h-2 rounded-full ${alertsSummary.critical > 0 ? 'bg-red-500 animate-pulse' : 'bg-orange-400'}`}
                                    aria-hidden="true"
                                />
                                <span className="text-xs text-slate-600">
                                    Je te sollicite aussi sur{' '}
                                    <strong className="font-semibold text-slate-900">
                                        {alertsSummary.unseen} alerte{alertsSummary.unseen > 1 ? 's' : ''}
                                    </strong>
                                    {alertsSummary.critical > 0 && (
                                        <span className="text-red-600 font-semibold">
                                            {' '}({alertsSummary.critical} critique{alertsSummary.critical > 1 ? 's' : ''})
                                        </span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={openAlerts}
                                    data-testid="uatu-alerts-nudge-open"
                                    className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline underline-offset-2"
                                >
                                    Voir →
                                </button>
                            </div>
                        )}

                        {/* CTA "Passe-moi la main" */}
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={() => {
                                    // Seed = résumé des priorités
                                    const topPriority = priorities[0];
                                    const seed = topPriority
                                        ? `Aide-moi à traiter mes ${total} priorités du jour, on commence par : ${topPriority.label}`
                                        : 'Fais un point rapide sur ma journée.';
                                    openChatWithSeed(seed);
                                }}
                                data-testid="uatu-give-me-the-lead"
                                className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold shadow-md hover:shadow-xl transition-all hover:scale-[1.02]"
                                style={{
                                    background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #A855F7 100%)',
                                }}
                            >
                                <Hand className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                                Passe-moi la main
                            </button>
                            <span className="ml-auto text-[10px] text-violet-400 italic flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Je surveille tout · à jour en temps réel
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export du seed opener pour que le nudge manager puisse l'utiliser
export function openUatuWithMessage(msg) {
    try {
        window.dispatchEvent(
            new CustomEvent('uatu:open-with-message', { detail: { message: msg || '' } })
        );
    } catch { /* noop */ }
}
